/**
 * Axios-Client + getypte Endpoint-Wrapper für das öffentliche Backend.
 *
 * Backend-Routes (siehe backend/app/main.py):
 *   /api/rooms             — Räume listen / per Slug
 *   /api/public/calendar   — Belegt/Geblockt-Feed
 *   /api/bookings          — POST anlegen (Mitglieder-only), POST /cancel
 */

import axios, { AxiosError, type AxiosInstance } from "axios";

import type {
  ApiErrorBody,
  BookingCalendarEvent,
  BookingCreateRequest,
  BookingCreateResponse,
  CancelResponse,
  MemberBookingItem,
  MemberLoginRequest,
  MemberProfile,
  MemberRegisterRequest,
  MemberRegisterResponse,
  MemberUpdateRequest,
  MemberVerifyResponse,
  PaymentInfo,
  Room,
  TokenResponse,
} from "@/types/api";

const MEMBER_TOKEN_KEY = "tanzraum.member.token";

export function getMemberToken(): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(MEMBER_TOKEN_KEY);
}

export function setMemberToken(token: string | null): void {
  if (typeof window === "undefined") return;
  if (token) {
    window.localStorage.setItem(MEMBER_TOKEN_KEY, token);
  } else {
    window.localStorage.removeItem(MEMBER_TOKEN_KEY);
  }
}

const baseURL = import.meta.env.VITE_API_BASE_URL ?? "/api";

export const http: AxiosInstance = axios.create({
  baseURL,
  timeout: 15_000,
  headers: { "Content-Type": "application/json" },
});

// Mitglieds-JWT automatisch als Bearer-Header anhängen für alle Mitglieder-Pfade.
// Buchungsanfragen sind seit MVP-Login-Pflicht ebenfalls authentifiziert.
const MEMBER_AUTH_PREFIXES = ["/member", "/bookings"];

http.interceptors.request.use((config) => {
  const token = getMemberToken();
  if (
    token &&
    config.url &&
    MEMBER_AUTH_PREFIXES.some((p) => config.url!.startsWith(p))
  ) {
    config.headers = config.headers ?? {};
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
});

export class ApiError extends Error {
  status: number;
  detail: ApiErrorBody["detail"];

  constructor(message: string, status: number, detail: ApiErrorBody["detail"]) {
    super(message);
    this.status = status;
    this.detail = detail;
  }
}

function toApiError(err: unknown): ApiError {
  if (err instanceof AxiosError) {
    const status = err.response?.status ?? 0;
    const detail =
      (err.response?.data as ApiErrorBody | undefined)?.detail ?? err.message;
    const msg = typeof detail === "string" ? detail : err.message;
    return new ApiError(msg, status, detail);
  }
  return new ApiError(String(err), 0, String(err));
}

async function unwrap<T>(promise: Promise<{ data: T }>): Promise<T> {
  try {
    const res = await promise;
    return res.data;
  } catch (err) {
    throw toApiError(err);
  }
}

// ── Rooms ────────────────────────────────────────────────────────────────
export const roomsApi = {
  list: () => unwrap<Room[]>(http.get("/rooms")),
  bySlug: (slug: string) =>
    unwrap<Room>(http.get(`/rooms/${encodeURIComponent(slug)}`)),
};

// ── Calendar ─────────────────────────────────────────────────────────────
export interface CalendarRangeQuery {
  starts_at: string;
  ends_at: string;
  room_id?: number;
}

export const calendarApi = {
  feed: (q: CalendarRangeQuery) =>
    unwrap<BookingCalendarEvent[]>(
      http.get("/public/calendar", {
        params: {
          starts_at: q.starts_at,
          ends_at: q.ends_at,
          ...(q.room_id ? { room_id: q.room_id } : {}),
        },
      }),
    ),
};

// ── Bookings ─────────────────────────────────────────────────────────────
export const bookingsApi = {
  create: (payload: BookingCreateRequest) =>
    unwrap<BookingCreateResponse>(http.post("/bookings", payload)),
  cancel: (token: string) =>
    unwrap<CancelResponse>(
      http.post("/bookings/cancel", null, { params: { token } }),
    ),
  paymentInfo: (token: string) =>
    unwrap<PaymentInfo>(http.get("/bookings/pay/info", { params: { token } })),
  /** Direkter Download-Link (.ics), nur für bestätigte Buchungen. */
  paymentIcsUrl: (token: string) =>
    `${baseURL}/bookings/pay/ics?token=${encodeURIComponent(token)}`,
};

// ── Member-Konto ─────────────────────────────────────────────────────────
export const memberApi = {
  register: (payload: MemberRegisterRequest) =>
    unwrap<MemberRegisterResponse>(http.post("/member/register", payload)),
  verify: (token: string) =>
    unwrap<MemberVerifyResponse>(
      http.get("/member/verify", { params: { token } }),
    ),
  login: (payload: MemberLoginRequest) =>
    unwrap<TokenResponse>(http.post("/member/login", payload)),
  me: () => unwrap<MemberProfile>(http.get("/member/me")),
  updateMe: (payload: MemberUpdateRequest) =>
    unwrap<MemberProfile>(http.patch("/member/me", payload)),
  bookings: () => unwrap<MemberBookingItem[]>(http.get("/member/bookings")),
  async downloadIcs(bookingId: number): Promise<Blob> {
    try {
      const res = await http.get(`/member/bookings/${bookingId}/ics`, {
        responseType: "blob",
      });
      return res.data as Blob;
    } catch (err) {
      throw toApiError(err);
    }
  },
};
